import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTransitionPresence } from "../../hooks/useTransitionPresence.js";
import { ControlContext } from "../../lib/control-context.js";
import { cn } from "../../lib/utils.js";
import { MOTION_DURATION_MS } from "../../motion.js";
import {
  anchorElement,
  focusAnchor,
  getLayerRoot,
  mergeRefs,
  useDismissable,
  useHoverIntent,
  useLayer,
  usePosition,
  useRovingTabIndex,
  type PositionAnchor,
  type PositionSide,
} from "../../primitives/index.js";
import {
  PopoverContext as Ctx,
  usePopoverContext as useCtx,
  type Align,
  type ContentRole,
  type FocusOnOpen,
} from "./popover-context.js";

/* Popover — first consumer of the overlay engine (strategy §14.4).
   Positioning, dismissal, and layer ordering all come from ../primitives;
   this file owns only menu semantics and skin. PopoverMenuItem lives in
   PopoverMenuItem.tsx and is re-exported below, so its import path is
   unchanged.
   states-check-ignore: this file renders no control of its own. The trigger
   is the caller's element, and the `[role="menuitem"]` strings are ring
   queries; PopoverMenuItem.tsx carries the item's focus and press states. */

export {
  PopoverMenuItem,
  type PopoverMenuItemProps,
  type PopoverMenuItemTone,
} from "./PopoverMenuItem.js";

/** An element, or a virtual rect: any object with `getBoundingClientRect()`. */
export type PopoverAnchor = PositionAnchor;

/** The side of the anchor the content opens on (Tooltip's word). */
export type PopoverPlacement = PositionSide;

/** What opens the Popover: a click only, or a resting mouse as well. */
export type PopoverOpenOn = "click" | "hover";

interface PopoverBaseProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: Align;
  /** Side of the anchor, flipped when it clips. Default "bottom", today's side. */
  placement?: PopoverPlacement | undefined;
  /** ARIA pattern of the content (drives role + aria-haspopup). Default "menu". */
  contentRole?: ContentRole | undefined;
  /**
   * `click` (default) opens on a click or the keyboard, as today. `hover` also
   * opens when a mouse rests on the anchor for `delayMs`, and closes
   * `closeDelayMs` after the pointer leaves both anchor and content. A click
   * pins it open until Esc, an outside press or a selection. Keyboard focus
   * opens a non-menu popover at once without moving focus; a menu still opens
   * only on Enter, Space or an Arrow key. Touch never opens it on hover, and
   * a virtual anchor has no node to hover.
   */
  openOn?: PopoverOpenOn | undefined;
  /** Hover only: how long a mouse rests before it opens, in ms. Default 300. */
  delayMs?: number | undefined;
  /** Hover only: the grace after the pointer leaves before it closes, in ms,
   *  so it can cross the gap into the content. Default 200. */
  closeDelayMs?: number | undefined;
  /** Optional className merged into the wrapper div around trigger+content.
   *  Use to override the default `relative inline-block` (e.g. when the
   *  wrapper itself must be a CSS Grid item that fills its track — pass
   *  `block w-full h-full` and a `wrapperStyle` with grid placement). */
  wrapperClassName?: string;
  /** Optional inline style merged into the wrapper div. Use for layout
   *  hints the wrapper must carry (gridRow/gridColumn, etc). */
  wrapperStyle?: React.CSSProperties;
  /** Non-editing reading content; canvas commands may ignore this layer.
   * Escape and outside press still dismiss it normally. */
  passive?: boolean;
  children: React.ReactNode;
}

/** No PopoverTrigger means nothing inside can open it, so `open` is controlled. */
interface PopoverAnchoredProps {
  /**
   * Place the content against this element or virtual rect instead of a
   * PopoverTrigger. An element anchor counts as inside for outside press and
   * gets focus back on Esc when it can take focus; a virtual rect does neither.
   */
  anchorRef: React.RefObject<PopoverAnchor | null>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export type PopoverProps = PopoverBaseProps &
  ({ anchorRef?: undefined } | PopoverAnchoredProps);

export function Popover({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  align = "end",
  placement = "bottom",
  contentRole = "menu",
  openOn = "click",
  delayMs = 300,
  closeDelayMs = 200,
  anchorRef,
  wrapperClassName,
  wrapperStyle,
  passive = false,
  children,
}: PopoverProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? !!openProp : internalOpen;
  const triggerRef = useRef<HTMLElement | null>(null);
  const anchor = anchorRef ?? triggerRef;
  const focusOnOpenRef = useRef<FocusOnOpen | null>(null);
  /** Set by an item that closed the menu by being selected. Consumed once below. */
  const restoreFocusOnCloseRef = useRef(false);
  const contentId = useId();
  const layerId = useLayer({ open, kind: passive ? "reading" : undefined });

  const setOpen = useCallback(
    (v: boolean) => {
      if (!isControlled) setInternalOpen(v);
      onOpenChange?.(v);
    },
    [isControlled, onOpenChange],
  );
  const hover = useHoverIntent({
    enabled: openOn === "hover",
    open,
    openDelayMs: delayMs,
    closeDelayMs,
    onOpenChange: setOpen,
  });

  /* Hover listens on the anchor element itself, so an external anchorRef
     hovers like a PopoverTrigger. No dependency list: the anchor can mount
     after the Popover, so every commit reads it again. */
  useEffect(() => {
    const el = openOn === "hover" ? anchorElement(anchor.current) : null;
    if (!el) return;
    const inside = (n: EventTarget | null) =>
      n instanceof Node &&
      (el.contains(n) || !!document.getElementById(contentId)?.contains(n));
    const on: Array<[string, (e: Event) => void]> = [
      ["pointerenter", (e) => hover.enter(e as PointerEvent)],
      ["pointerleave", (e) => hover.leave(e as PointerEvent)],
      ["pointerdown", () => hover.skipFocus()],
      [
        "focusin",
        () => {
          if (contentRole !== "menu") hover.focus();
        },
      ],
      [
        "focusout",
        (e) => {
          if (!inside((e as FocusEvent).relatedTarget)) hover.blur();
        },
      ],
    ];
    for (const [type, fn] of on) el.addEventListener(type, fn);
    return () => {
      for (const [type, fn] of on) el.removeEventListener(type, fn);
    };
  });

  /* Restore focus to the anchor (the trigger) after a SELECT closes the menu.
     
     A layout effect rather than the item's click handler, because a menu item may
     destroy its own trigger — "Remove" acting on the row that owns the menu is the
     common shape. Inside the handler React has not committed that removal yet, so
     `isConnected` still reads true and focusing there sends focus to <body> the
     moment the row unmounts, silently beating the consumer's own focus management
     to a control that survives.
     
     Layout effects run after the DOM mutation but still inside React's flush, so
     the surviving-trigger case stays synchronous for callers (and their tests). */
  useLayoutEffect(() => {
    if (open || !restoreFocusOnCloseRef.current) return;
    restoreFocusOnCloseRef.current = false;
    hover.skipFocus();
    focusAnchor(anchor.current);
  }, [open, anchor, hover]);

  const ctx = useMemo(
    () => ({
      open,
      setOpen,
      triggerRef,
      anchorRef: anchor,
      contentId,
      align,
      placement,
      contentRole,
      layerId,
      focusOnOpenRef,
      restoreFocusOnCloseRef,
      hover,
    }),
    [
      open,
      setOpen,
      anchor,
      contentId,
      align,
      placement,
      contentRole,
      layerId,
      hover,
    ],
  );

  return (
    <Ctx.Provider value={ctx}>
      <div
        className={cn("relative inline-block", wrapperClassName)}
        style={wrapperStyle}
      >
        {children}
      </div>
    </Ctx.Provider>
  );
}

type TriggerElement = React.ReactElement<
  React.HTMLAttributes<HTMLElement> & { ref?: React.Ref<HTMLElement> }
>;

export interface PopoverTriggerProps {
  children: TriggerElement;
  "aria-describedby"?: string | undefined;
}

export function PopoverTrigger({
  children,
  "aria-describedby": ariaDescribedBy,
}: PopoverTriggerProps) {
  const {
    open,
    setOpen,
    triggerRef,
    contentId,
    contentRole,
    focusOnOpenRef,
    hover,
  } = useCtx();
  const child = React.Children.only(children);
  const childRef = (child as TriggerElement & { ref?: React.Ref<HTMLElement> })
    .ref;
  return React.cloneElement(child, {
    ref: mergeRefs<HTMLElement>(triggerRef, childRef),
    "aria-haspopup": contentRole,
    "aria-expanded": open,
    "aria-controls": open ? contentId : undefined,
    "aria-describedby": ariaDescribedBy ?? child.props["aria-describedby"],
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      child.props.onClick?.(e);
      if (e.defaultPrevented) return;
      /* Hover: a click pins a closed or hover-opened popover open. */
      if (!hover.pin()) setOpen(!open);
      else if (!open) setOpen(true);
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
      child.props.onKeyDown?.(e);
      if (e.defaultPrevented) return;
      /* Enter/Space: the native button click follows and does the opening — this only
         records that the open was keyboard-driven, so focus moves into the menu. */
      if (e.key === "Enter" || e.key === " ") {
        focusOnOpenRef.current = "seed";
        return;
      }
      /* ArrowUp means "last item" and outranks the seed; ArrowDown takes the seed. */
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        focusOnOpenRef.current = e.key === "ArrowUp" ? "last" : "seed";
        hover.pin();
        if (!open) setOpen(true);
      }
    },
  });
}

/** The content's inner padding, from the named scale. */
export type PopoverPadding = "none" | "xs" | "sm" | "md" | "lg";

/* Padding only: surface, radius, border, min-width and motion never change
   with it. `xs` (p-1) is the menu padding and the default; `md` (p-3) is the
   panel padding FilterMenu uses. */
const PADDING: Record<PopoverPadding, string> = {
  none: "p-0",
  xs: "p-1",
  sm: "p-2",
  md: "p-3",
  lg: "p-4",
};

/** The content's surface (design-owner decision 17). */
export type PopoverSurface = "default" | "rail";

/* Corner, border colour, fill, shadow and hairline; the rest of the class
   string never changes with it. `rail` is the Player AI rail surface that
   PlayerAIPopoverShell draws today. */
const SURFACE: Record<PopoverSurface, string> = {
  default:
    "rounded-md border border-pb-border bg-pb-card-elevated text-pb-text shadow-lg pb-hairline",
  rail: "rounded-xl sm:rounded-2xl border border-pb-border-strong bg-pb-rail text-pb-text shadow-2xl",
};

/* The rail surface keeps PlayerAIPopoverShell's layer, z-40, so a dialog it
   opens (z-overlay) draws above it, as today (decision 17, harness d13). */
const LAYER: Record<PopoverSurface, string> = {
  default: "z-popover",
  rail: "z-40",
};

/** Who places the content: the anchor (`usePosition`) or the caller's classes. */
export type PopoverPositioning = "anchor" | "none";

export interface PopoverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Inner padding: `none` | `xs` | `sm` | `md` | `lg` = p-0 | p-1 | p-2 | p-3 |
   * p-4. Default `xs`, today's menu padding. `className` carries width and
   * layout; a padding class there still wins.
   */
  padding?: PopoverPadding | undefined;
  /**
   * `default` is the popover surface. `rail` swaps its corner, border colour,
   * fill, shadow and hairline for the Player AI rail surface (decision 17),
   * and draws at the rail's `z-40` instead of `z-popover`, so a dialog opened
   * from it stays on top.
   */
  surface?: PopoverSurface | undefined;
  /**
   * `anchor` (default) sets an inline `position: fixed` with `top` and `left`
   * from the anchor. `none` sets no position style, so `className` places the
   * content; `sideOffset` and `maxHeight` then do nothing.
   */
  positioning?: PopoverPositioning | undefined;
  sideOffset?: number;
  /** Size the dropdown to its anchor, using the shared positioning engine. */
  matchWidth?: boolean;
  /** Cap the content height (px). Prefer this over a CSS max-height: the
   *  positioner places the box against its real rendered height, so a tall
   *  menu opening ABOVE a bottom-anchored trigger stays glued to it (a CSS
   *  max-height would leave it floating by scrollHeight − cap). */
  maxHeight?: number;
  /**
   * Which item a KEYBOARD open lands on, defaulting to the first.
   *
   * Indexes the enabled-menuitem ring discovered at runtime — which equals a caller's
   * own item index only while none of its items carry the hard `disabled` attribute
   * (`aria-disabled` items stay in the ring; hard-disabled ones are excluded).
   */
  initialActiveIndex?: number;
}

export function PopoverContent({
  className,
  children,
  sideOffset = 8,
  matchWidth = false,
  maxHeight,
  style,
  initialActiveIndex,
  padding = "xs",
  surface = "default",
  positioning = "anchor",
  ...rest
}: PopoverContentProps) {
  const {
    open,
    setOpen,
    anchorRef,
    contentId,
    align,
    placement,
    contentRole,
    layerId,
    focusOnOpenRef,
    hover,
  } = useCtx();
  const ref = useRef<HTMLDivElement>(null);
  const { present, phase } = useTransitionPresence(
    open,
    MOTION_DURATION_MS.state,
  );
  const transitioningOut = phase === "closing";

  // The closing tree stays mounted for its visual exit. Remove it from every
  // interaction path before paint while aria-hidden removes it from the
  // accessibility tree. React 18 serializes inert={false}, so toggle the
  // attribute imperatively instead of leaving a false attribute behind.
  useLayoutEffect(() => {
    if (
      !open &&
      contentRole === "dialog" &&
      ref.current?.contains(document.activeElement)
    ) {
      hover.skipFocus();
      focusAnchor(anchorRef.current);
    }
    ref.current?.toggleAttribute("inert", transitioningOut);
  }, [open, contentRole, transitioningOut, hover, anchorRef]);

  const insideRefs = useMemo(() => [ref, anchorRef], [anchorRef]);
  const onDismiss = useCallback(
    (reason: "escape" | "outside-press") => {
      setOpen(false);
      if (reason !== "escape") return;
      hover.skipFocus();
      focusAnchor(anchorRef.current);
    },
    [setOpen, anchorRef, hover],
  );
  useDismissable({ layerId, open, onDismiss, insideRefs });

  const pos = usePosition({
    open: present && positioning === "anchor",
    triggerRef: anchorRef,
    contentRef: ref,
    side: placement,
    align,
    sideOffset,
    matchWidth,
    ...(maxHeight != null ? { maxHeight } : {}),
  });

  /* ---- APG menu keyboard contract, wired only when this content IS a menu ----
     Auto-detected from the rendered children rather than a prop, so all 16 existing
     menus get arrow keys with no call-site churn, and any popover holding no
     menuitems (every `dialog`, plus a `menu` whose items are all hard-disabled)
     keeps today's behaviour exactly. */
  const itemsRef = useRef<HTMLElement[]>([]);
  const [itemCount, setItemCount] = useState(0);
  /* Named for the DOM attribute it must NOT be confused with. Focus follows the roving
     index only when the last input was a key: expressing activity as real DOM focus on
     `mouseenter` (as ContextMenu does) would yank focus into the portal on hover. */
  const lastInputRef = useRef<"key" | "pointer">("pointer");

  const {
    activeIndex,
    setActiveIndex,
    onKeyDown: onRovingKeyDown,
  } = useRovingTabIndex({ itemCount, loop: true, orientation: "vertical" });

  useLayoutEffect(() => {
    if (!open || contentRole !== "menu" || !ref.current) {
      itemsRef.current = [];
      setItemCount(0);
      return;
    }
    const found = Array.from(
      ref.current.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([disabled])',
      ),
    );
    itemsRef.current = found;
    setItemCount(found.length);
  }, [open, contentRole, children]);

  /* Seed the ring on a keyboard open. Deliberately does NOT clear the flag until the
     items exist — this effect first runs with itemCount 0, before the layout effect
     above has measured, and clearing there would swallow the intent. */
  useEffect(() => {
    const want = focusOnOpenRef.current;
    if (!open || !want || (positioning === "anchor" && !pos)) return;
    if (contentRole !== "menu") {
      const target = Array.from(
        ref.current?.querySelectorAll<HTMLElement>(
          "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]",
        ) ?? [],
      ).find(
        (element) =>
          element.tabIndex >= 0 &&
          !element.closest('[inert], [aria-hidden="true"]'),
      );
      if (!target) return;
      focusOnOpenRef.current = null;
      target.focus();
      return;
    }
    if (itemCount === 0) return;
    focusOnOpenRef.current = null;
    lastInputRef.current = "key";
    setActiveIndex(
      want === "last"
        ? itemCount - 1
        : Math.min(Math.max(initialActiveIndex ?? 0, 0), itemCount - 1),
    );
  }, [
    open,
    itemCount,
    initialActiveIndex,
    setActiveIndex,
    focusOnOpenRef,
    contentRole,
    positioning,
    pos,
  ]);

  useEffect(() => {
    itemsRef.current.forEach((el, i) => {
      el.tabIndex = i === activeIndex ? 0 : -1;
    });
    if (open && itemCount > 0 && lastInputRef.current === "key") {
      itemsRef.current[activeIndex]?.focus();
    }
  }, [open, activeIndex, itemCount]);

  useEffect(() => {
    if (!open) {
      lastInputRef.current = "pointer";
      focusOnOpenRef.current = null;
      setActiveIndex(0);
    }
  }, [open, setActiveIndex, focusOnOpenRef]);

  const onContentKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    rest.onKeyDown?.(e);
    if (e.defaultPrevented || itemCount === 0) return;
    /* A nested layer — a dialog popover or a modal opened from inside this content —
       is portalled elsewhere in the DOM but stays a React child, so its key events
       bubble here through the React tree. They belong to that layer: without this
       guard a Tab in a nested confirm closed this menu too, and arrows moved its ring. */
    if (!(e.target instanceof Node) || !ref.current?.contains(e.target)) return;
    if (e.key === "Tab") {
      /* The content is portalled to the end of <body>, so an un-trapped Tab walks the
         rest of the page rather than the menu. */
      e.preventDefault();
      setOpen(false);
      focusAnchor(anchorRef.current);
      return;
    }
    if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      lastInputRef.current = "key";
      setActiveIndex(e.key === "Home" ? 0 : itemCount - 1);
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp")
      lastInputRef.current = "key";
    onRovingKeyDown(e);
  };

  /* Delegated rather than per-item, because children are arbitrary JSX. Syncs the ring
     so a subsequent arrow continues from the hovered row — without moving focus.

     A POINTER event, not onMouseOver: the DS already models dismissal on pointerdown,
     and jsx-a11y/mouse-events-have-key-events would (rightly) demand an onFocus twin for
     a mouse-only affordance. The keyboard twin here is the arrow handling on this very
     element, which does exactly what the hover does — so the parity the rule asks for
     exists; it just is not expressible as onFocus. */
  const onContentPointerOver = (
    e: React.PointerEvent<HTMLDivElement>,
  ): void => {
    rest.onPointerOver?.(e);
    if (itemCount === 0) return;
    const item = (e.target as HTMLElement).closest?.('[role="menuitem"]');
    const idx = item ? itemsRef.current.indexOf(item as HTMLElement) : -1;
    if (idx >= 0 && idx !== activeIndex) {
      lastInputRef.current = "pointer";
      setActiveIndex(idx);
    }
  };

  if (!present) return null;
  if (typeof document === "undefined") return null;

  const node = (
    <div
      ref={ref}
      id={contentId}
      role={contentRole}
      aria-hidden={transitioningOut ? "true" : undefined}
      data-motion-state={phase}
      style={
        positioning === "anchor"
          ? {
              position: "fixed",
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              width: pos?.width,
              visibility: pos ? "visible" : "hidden",
              maxHeight: pos?.maxHeight,
              overflowY: pos?.maxHeight ? "auto" : undefined,
              ...style,
            }
          : style
      }
      className={cn(
        LAYER[surface],
        "min-w-30",
        SURFACE[surface],
        PADDING[padding],
        /* The anchored-overlay grade (ADR-0091). This component was where the
           pattern was written first; it now names it, so every listbox, menu
           and picker that adopted `useOverlayPresence` moves identically. */
        "origin-top pb-motion-overlay pb-motion-origin-anchored",
        className,
      )}
      {...rest}
      onKeyDown={onContentKeyDown}
      onPointerOver={onContentPointerOver}
      onPointerEnter={(e) => {
        rest.onPointerEnter?.(e);
        hover.enter(e);
      }}
      onPointerLeave={(e) => {
        rest.onPointerLeave?.(e);
        hover.leave(e);
      }}
    >
      {/* Portalled out of any rail group: its Buttons keep their own look. */}
      <ControlContext.Provider value={null}>{children}</ControlContext.Provider>
    </div>
  );

  return createPortal(node, getLayerRoot());
}

export function PopoverSeparator({ className }: { className?: string }) {
  return <div className={cn("my-1 h-px bg-pb-border", className)} />;
}
